<template>
    <div class="aa-wrapper">

        <div v-if="loading" class="aa-state">
            <div class="aa-state__spinner">
                <span class="spinner-border spinner-border-sm"></span>
            </div>
            <span>Carregando avisos...</span>
        </div>

        <div v-else-if="error" class="aa-state aa-state--error">
            <i class="fas fa-exclamation-triangle"></i>
            <span>Falha ao buscar avisos ativos</span>
        </div>

        <div v-else-if="!rows.length" class="aa-state aa-state--empty">
            <i class="fas fa-check-circle"></i>
            <span>Nenhum aviso ativo na URA</span>
        </div>

        <template v-else>
            <div class="aa-table-wrap">
                <table class="aa-table">
                    <thead>
                        <tr>
                            <th>OPÇÃO</th>
                            <th>FILA</th>
                            <th>MOTIVO</th>
                            <th>ÁUDIO</th>
                            <th>STATUS</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, idx) in rows"
                            :key="`${row.avisoId}-${idx}`"
                            :class="{ 'tr--off': !row.active }"
                        >
                            <td>
                                <span class="chip">{{ row.option }}</span>
                            </td>

                            <td class="tc">{{ row.queue }}</td>

                            <td class="tc">{{ row.reason || '—' }}</td>

                            <td class="td-player">
                                <template v-if="row.audio">
                                    <audio
                                        :ref="`aud_${idx}`"
                                        :src="`uraAudios/${row.audio}`"
                                        preload="metadata"
                                        @loadedmetadata="onMeta(idx)"
                                        @timeupdate="onTick(idx)"
                                        @ended="onEnd(idx)"
                                    ></audio>

                                    <div class="player">
                                        <button
                                            type="button"
                                            class="p-play"
                                            @click="togglePlay(idx)"
                                        >
                                            <i :class="row._playing ? 'fas fa-pause' : 'fas fa-play'"></i>
                                        </button>

                                        <div
                                            class="p-track"
                                            :class="{ 'is-dragging': dragging === idx }"
                                            :ref="`track_${idx}`"
                                            @mousedown="startDrag(idx, $event)"
                                            @touchstart.prevent="startDrag(idx, $event)"
                                        >
                                            <div
                                                class="p-fill"
                                                :class="{ 'no-transition': dragging === idx }"
                                                :style="{ width: visualPct(row) + '%' }"
                                            ></div>
                                            <div
                                                class="p-thumb"
                                                :class="{ 'thumb-visible': dragging === idx }"
                                                :style="{ left: visualPct(row) + '%' }"
                                            ></div>
                                        </div>

                                        <span class="p-time">
                                            {{ fmt(row._cur) }}<span class="p-sep">/</span>{{ fmt(row._dur) }}
                                        </span>

                                        <div
                                            class="vol-wrap"
                                            @mouseenter="openVolume(idx)"
                                            @mouseleave="scheduleClose(idx)"
                                        >
                                            <transition name="vol-slide">
                                                <div
                                                    v-if="row._volOpen"
                                                    class="vol-slider-wrap"
                                                    @mouseenter="cancelClose(idx)"
                                                    @mouseleave="scheduleClose(idx)"
                                                >
                                                    <div
                                                        class="vol-track"
                                                        :class="{ 'is-dragging': volDragging === idx }"
                                                        :ref="`vol_${idx}`"
                                                        @mousedown="startVolDrag(idx, $event)"
                                                        @touchstart.prevent="startVolDrag(idx, $event)"
                                                    >
                                                        <div
                                                            class="vol-fill"
                                                            :class="{ 'no-transition': volDragging === idx }"
                                                            :style="{ width: volPct(row) + '%' }"
                                                        ></div>
                                                        <div
                                                            class="vol-thumb"
                                                            :class="{ 'thumb-visible': volDragging === idx }"
                                                            :style="{ left: volPct(row) + '%' }"
                                                        ></div>
                                                    </div>
                                                    <span class="vol-pct">{{ Math.round((row._vol ?? 1) * 100) }}%</span>
                                                </div>
                                            </transition>
                                            <button
                                                type="button"
                                                class="p-vol"
                                                @click="toggleMute(idx)"
                                                :title="row._muted ? 'Desmutar' : 'Mutar'"
                                            >
                                                <i :class="volIcon(row)"></i>
                                            </button>
                                        </div>
                                    </div>
                                </template>
                                <span v-else class="no-audio">—</span>
                            </td>

                            <td class="td-status">
                                <label class="tgl">
                                    <input
                                        type="checkbox"
                                        :checked="row.active"
                                        @change="setActive(idx, $event.target.checked)"
                                    />
                                    <span class="tgl-track">
                                        <span class="tgl-knob"></span>
                                        <span class="tgl-lbl">{{ row.active ? 'ON' : 'OFF' }}</span>
                                    </span>
                                </label>
                                <transition name="fade">
                                    <div v-if="!row.active" class="off-hint">
                                        <i class="fas fa-power-off"></i> Desativar
                                    </div>
                                </transition>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <transition name="slide-up">
                <div v-if="pendingCount > 0" class="aa-footer">
                    <span class="pending-badge">{{ pendingCount }} para encerrar</span>
                    <button
                        type="button"
                        class="btn-encerrar"
                        :disabled="saving"
                        @click="encerrar"
                    >
                        <template v-if="saving">
                            <span class="spinner-border spinner-border-sm"></span>
                            Encerrando...
                        </template>
                        <template v-else>
                            <i class="fas fa-power-off"></i>
                            Encerrar Selecionados
                        </template>
                    </button>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
export default {
    name: 'AvisosAtivos',

    data() {
        return {
            rows:       [],
            loading:    false,
            error:      false,
            saving:     false,
            dragging:   null,
            volDragging: null,
        };
    },

    computed: {
        pendingCount() {
            return this.rows.filter(r => !r.active).length;
        },
    },

    mounted() {
        this.fetchAvisos();
    },

    methods: {

        async fetchAvisos() {
            this.loading = true;
            this.error   = false;
            try {
                const { data } = await axios.get('ura_management/avisosAtivas');
                this.rows = this.normalize(data);
            } catch (e) {
                console.error('[AvisosAtivos]', e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },

        normalize(resp) {
            if (!Array.isArray(resp) || !resp.length) return [];
            const out = [];
            resp.forEach(aviso => {
                (aviso.data || []).forEach(el => {
                    out.push({
                        avisoId:  aviso.id,
                        option:   el?.option ?? '—',
                        queue:    el?.queue  ?? '—',
                        reason:   el?.reason ?? '',
                        audio:    el?.audio  ?? '',
                        active:   true,
                        _playing: false,
                        _muted:   false,
                        _vol:     1,
                        _volOpen: false,
                        _cur:     0,
                        _dur:     0,
                    });
                });
            });
            return out;
        },

        setRowProp(idx, key, val) {
            this.$set(this.rows, idx, { ...this.rows[idx], [key]: val });
        },

        setActive(idx, checked) {
            this.setRowProp(idx, 'active', checked);
        },

        audio(idx) {
            const r = this.$refs[`aud_${idx}`];
            return Array.isArray(r) ? r[0] : r;
        },

        togglePlay(idx) {
            const a = this.audio(idx);
            if (!a) return;
            if (this.rows[idx]._playing) {
                a.pause();
                this.setRowProp(idx, '_playing', false);
            } else {
                this.rows.forEach((r, i) => {
                    if (i !== idx && r._playing) {
                        const other = this.audio(i);
                        if (other) other.pause();
                        this.setRowProp(i, '_playing', false);
                    }
                });
                a.play();
                this.setRowProp(idx, '_playing', true);
            }
        },

        toggleMute(idx) {
            const a = this.audio(idx);
            if (!a) return;
            const muted = !this.rows[idx]._muted;
            a.muted = muted;
            this.setRowProp(idx, '_muted', muted);
        },

        volIcon(row) {
            if (row._muted || row._vol === 0) return 'fas fa-volume-mute';
            if (row._vol < 0.5)              return 'fas fa-volume-down';
            return 'fas fa-volume-up';
        },

        volPct(row) {
            return Math.round((row._vol != null ? row._vol : 1) * 100);
        },

        openVolume(idx) {
            this.cancelClose(idx);
            this.rows.forEach((r, i) => {
                if (i !== idx && r._volOpen) this.setRowProp(i, '_volOpen', false);
            });
            this.setRowProp(idx, '_volOpen', true);
        },

        scheduleClose(idx) {
            if (this.volDragging === idx) return;
            this._closeTimer = setTimeout(() => {
                this.setRowProp(idx, '_volOpen', false);
            }, 180);
        },

        cancelClose(idx) {
            clearTimeout(this._closeTimer);
        },

        getVolTrack(idx) {
            const r = this.$refs[`vol_${idx}`];
            return Array.isArray(r) ? r[0] : r;
        },

        applyVolume(idx, clientX) {
            const track = this.getVolTrack(idx);
            if (!track) return;
            const rect = track.getBoundingClientRect();
            const pct  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            const a    = this.audio(idx);
            if (a) {
                a.volume = pct;
                a.muted  = pct === 0;
            }
            this.$set(this.rows, idx, {
                ...this.rows[idx],
                _vol:   pct,
                _muted: pct === 0,
            });
        },

        startVolDrag(idx, event) {
            this.volDragging = idx;
            event.preventDefault();
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            this.applyVolume(idx, clientX);

            const onMove = (e) => {
                const cx = e.touches ? e.touches[0].clientX : e.clientX;
                this.applyVolume(idx, cx);
            };
            const onUp = () => {
                this.volDragging = null;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup',   onUp);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend',  onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup',   onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend',  onUp);
        },

        onMeta(idx) {
            const a = this.audio(idx);
            if (a) this.setRowProp(idx, '_dur', a.duration || 0);
        },

        onTick(idx) {
            const a = this.audio(idx);
            if (a) this.setRowProp(idx, '_cur', a.currentTime);
        },

        onEnd(idx) {
            this.setRowProp(idx, '_playing', false);
            this.setRowProp(idx, '_cur', 0);
            const a = this.audio(idx);
            if (a) a.currentTime = 0;
        },

        getTrack(idx) {
            const r = this.$refs[`track_${idx}`];
            return Array.isArray(r) ? r[0] : r;
        },

        pctFromMouse(idx, clientX) {
            const track = this.getTrack(idx);
            if (!track) return 0;
            const rect = track.getBoundingClientRect();
            return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        },

        startDrag(idx, event) {
            this.dragging = idx;
            event.preventDefault();
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            const pct = this.pctFromMouse(idx, clientX);
            this.setRowProp(idx, '_cur', pct * (this.rows[idx]._dur || 0));

            const onMove = (e) => {
                const cx = e.touches ? e.touches[0].clientX : e.clientX;
                const p  = this.pctFromMouse(idx, cx);
                this.$set(this.rows, idx, { ...this.rows[idx], _cur: p * (this.rows[idx]._dur || 0) });
            };

            const onUp = () => {
                const a = this.audio(idx);
                if (a) a.currentTime = this.rows[idx]._cur;
                this.dragging = null;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup',   onUp);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend',  onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup',   onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend',  onUp);
        },

        visualPct(row) {
            if (!row._dur) return 0;
            return Math.min(100, (row._cur / row._dur) * 100);
        },

        fmt(sec) {
            if (!sec || isNaN(sec)) return '0:00';
            const m = Math.floor(sec / 60);
            const s = Math.floor(sec % 60).toString().padStart(2, '0');
            return `${m}:${s}`;
        },

        async encerrar() {
            const ids = [...new Set(
                this.rows.filter(r => !r.active).map(r => r.avisoId)
            )];
            if (!ids.length) return;

            const result = await Swal.fire({
                title: `Encerrar ${ids.length} aviso(s)?`,
                text:  'Esta ação não pode ser desfeita.',
                type:  'warning',
                showCancelButton:   true,
                confirmButtonText:  'Sim, encerrar',
                cancelButtonText:   'Cancelar',
                confirmButtonColor: '#c0392b',
                cancelButtonColor:  '#6c757d',
            });

            if (!result.value) {
                this.rows.forEach((r, i) => {
                    if (!r.active) this.setRowProp(i, 'active', true);
                });
                return;
            }

            this.saving = true;
            try {
                await axios.post('ura_management/disableAviso', { data: ids });
                await Swal.fire({
                    type:             'success',
                    title:            'Aviso(s) encerrado(s)!',
                    showConfirmButton: false,
                    timer:            1500,
                });
                await this.fetchAvisos();
            } catch (e) {
                console.error('[AvisosAtivos] encerrar:', e);
                Swal.fire({ type: 'error', title: 'Erro ao encerrar aviso(s).' });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.aa-wrapper {
    font-size: 0.82rem;
    color: #2c3e50;
    overflow: visible;
}

.aa-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 28px 16px;
    font-size: 0.88rem;
    color: #aaa;

    &--error { color: #e74c3c; }
    &--empty {
        color: #27ae60;
        i { font-size: 1.3rem; }
    }
    &__spinner { display: flex; align-items: center; }
}

.aa-table-wrap { overflow: visible; }

.aa-table {
    width: 100%;
    border-collapse: collapse;

    thead tr {
        background: #1c869a;
        color: #fff;

        th {
            padding: 10px 14px;
            text-align: center;
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            white-space: nowrap;
        }
    }

    tbody tr {
        border-bottom: 1px solid #edf2f4;
        transition: background 0.15s;

        &:last-child { border-bottom: none; }
        &:hover { background: #f5fbfc; }

        &.tr--off {
            background: #fff8f8;
            opacity: 0.65;
        }

        td {
            padding: 11px 14px;
            vertical-align: middle;
            overflow: visible;
        }
    }
}

.tc { text-align: center; }

.chip {
    display: inline-block;
    background: linear-gradient(135deg, #e8f7fa 0%, #d0eef4 100%);
    color: #1c869a;
    border: 1px solid #9dd5e0;
    border-radius: 99px !important;
    padding: 4px 14px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.td-player { min-width: 260px; overflow: visible; }

.player {
    display: flex;
    align-items: center;
    gap: 9px;
    background: #fff;
    border: 1px solid #d6eef3;
    border-radius: 99px !important;
    padding: 6px 10px 6px 6px;
    box-shadow: 0 1px 4px rgba(28, 134, 154, 0.08);
    position: relative;
    overflow: visible;
}

.p-play {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50% !important;
    border: none;
    background: #1c869a;
    color: #fff;
    font-size: 0.68rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(28, 134, 154, 0.4);
    transition: background 0.18s, transform 0.1s, box-shadow 0.18s;

    &:hover {
        background: #166f80;
        box-shadow: 0 3px 10px rgba(28, 134, 154, 0.5);
    }
    &:active { transform: scale(0.92); }
    i { pointer-events: none; }
}

.p-track {
    flex: 1;
    position: relative;
    height: 5px;
    background: #ddf0f4;
    border-radius: 5px !important;
    cursor: grab;
    min-width: 60px;
    user-select: none;

    &.is-dragging { cursor: grabbing; }

    &:hover .p-thumb,
    &.is-dragging .p-thumb { opacity: 1; }
}

.p-fill {
    position: absolute;
    left: 0; top: 0;
    height: 100%;
    background: linear-gradient(90deg, #1c869a, #27b3ce);
    border-radius: 5px !important;
    transition: width 0.08s linear;
    pointer-events: none;

    &.no-transition { transition: none !important; }
}

.p-thumb {
    position: absolute;
    top: 50%;
    margin-top: -7px;
    transform: translateX(-50%);
    width: 14px;
    height: 14px;
    border-radius: 50% !important;
    background: #1c869a;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px #1c869a;
    opacity: 0;
    transition: opacity 0.15s, width 0.1s, height 0.1s, margin-top 0.1s, box-shadow 0.15s;
    pointer-events: none;

    &.thumb-visible {
        opacity: 1;
        width: 16px;
        height: 16px;
        margin-top: -8px;
        box-shadow: 0 0 0 3px rgba(28,134,154,0.25), 0 0 0 2px #1c869a;
    }
}

.p-time {
    white-space: nowrap;
    font-size: 0.65rem;
    color: #7f8c8d;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.p-sep { margin: 0 2px; opacity: 0.5; }

.vol-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    overflow: visible;
    gap: 0;
}

.p-vol {
    background: transparent;
    border: none;
    color: #1c869a;
    font-size: 0.82rem;
    cursor: pointer;
    padding: 0 3px;
    opacity: 0.7;
    transition: opacity 0.15s;
    line-height: 1;
    flex-shrink: 0;
    position: relative;
    z-index: 1;

    &:hover { opacity: 1; }
}

.vol-slider-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0 6px 0 0;
    user-select: none;
    pointer-events: all;
    overflow: hidden;
    max-width: 120px;
}

.vol-pct {
    font-size: 0.62rem;
    font-weight: 700;
    color: #7f8c8d;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    min-width: 26px;
    text-align: right;
}

.vol-track {
    position: relative;
    height: 4px;
    width: 64px;
    background: #ddf0f4;
    border-radius: 4px !important;
    cursor: grab;
    flex-shrink: 0;

    &.is-dragging { cursor: grabbing; }

    &:hover .vol-thumb,
    &.is-dragging .vol-thumb { opacity: 1; }
}

.vol-fill {
    position: absolute;
    top: 0; left: 0;
    height: 100%;
    background: linear-gradient(90deg, #1c869a, #27b3ce);
    border-radius: 4px !important;
    transition: width 0.06s linear;
    pointer-events: none;

    &.no-transition { transition: none !important; }
}

.vol-thumb {
    position: absolute;
    top: 50%;
    margin-top: -5.5px;
    transform: translateX(-50%);
    width: 11px;
    height: 11px;
    border-radius: 50% !important;
    background: #1c869a;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1.5px #1c869a;
    opacity: 0;
    transition: opacity 0.15s, width 0.1s, height 0.1s, box-shadow 0.15s;
    pointer-events: none;

    &.thumb-visible {
        opacity: 1;
        width: 13px;
        height: 13px;
        margin-top: -6.5px;
        box-shadow: 0 0 0 2.5px rgba(28,134,154,0.25), 0 0 0 1.5px #1c869a;
    }
}

.no-audio { color: #ccc; }

.td-status {
    text-align: center;
    min-width: 100px;
}

.tgl {
    display: inline-flex;
    cursor: pointer;
    user-select: none;

    input {
        position: absolute;
        opacity: 0;
        width: 0; height: 0;
    }
}

.tgl-track {
    position: relative;
    width: 56px;
    height: 26px;
    background: #ccc;
    border-radius: 26px !important;
    transition: background 0.28s;
    box-shadow: inset 0 1px 3px rgba(0,0,0,0.15);
}

.tgl input:checked + .tgl-track { background: #1c869a; }

.tgl-knob {
    position: absolute;
    top: 3px; left: 3px;
    width: 20px; height: 20px;
    background: #fff;
    border-radius: 50% !important;
    transition: transform 0.28s cubic-bezier(.34,1.56,.64,1);
    box-shadow: 0 1px 4px rgba(0,0,0,0.22);
    z-index: 1;
}

.tgl input:checked + .tgl-track .tgl-knob { transform: translateX(30px); }

.tgl-lbl {
    position: absolute;
    top: 50%; right: 7px;
    transform: translateY(-50%);
    font-size: 8.5px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.04em;
    pointer-events: none;
    transition: left 0.28s, right 0.28s;
}

.tgl input:checked + .tgl-track .tgl-lbl { left: 9px; right: auto; }

.off-hint {
    margin-top: 4px;
    font-size: 0.67rem;
    color: #e74c3c;
    font-weight: 700;
    i { margin-right: 2px; }
}

.aa-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 12px 6px 6px;
    border-top: 1px dashed #e0ecef;
    margin-top: 6px;
}

.pending-badge {
    background: #fdecea;
    color: #c0392b;
    border: 1px solid #f5c6c2;
    border-radius: 99px !important;
    padding: 3px 12px;
    font-size: 0.75rem;
    font-weight: 700;
}

.btn-encerrar {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #c0392b;
    color: #fff;
    border: none;
    border-radius: 8px !important;
    padding: 8px 20px;
    font-size: 0.83rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(192, 57, 43, 0.35);
    transition: background 0.18s, box-shadow 0.18s, transform 0.1s;

    &:hover:not(:disabled) {
        background: #a93226;
        box-shadow: 0 3px 12px rgba(192, 57, 43, 0.45);
    }
    &:active:not(:disabled) { transform: scale(0.97); }
    &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.slide-up-enter-active { transition: all 0.25s ease; }
.slide-up-leave-active { transition: all 0.2s ease; }
.slide-up-enter        { opacity: 0; transform: translateY(8px); }
.slide-up-leave-to     { opacity: 0; transform: translateY(4px); }

.vol-slide-enter-active { transition: opacity 0.18s ease, transform 0.18s ease, max-width 0.2s ease; }
.vol-slide-leave-active { transition: opacity 0.14s ease, transform 0.14s ease, max-width 0.16s ease; }
.vol-slide-enter        { opacity: 0; transform: translateX(8px); max-width: 0; }
.vol-slide-leave-to     { opacity: 0; transform: translateX(6px); max-width: 0; }

</style>